
import React, { useEffect, useState, useCallback } from 'react';
import SingleDocUpload from './Admin'; // Assuming Admin.js is the correct component name
import axios from 'axios';
// Removed Heroicons imports as the reference pagination doesn't use them directly for simple prev/next text

const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/v1/user`;

const ManageDoc = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [docData, setDocData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalItems, setTotalItems] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10); // You can make this configurable
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');


    const handleOpenModalForAdd = () => {
        setError('');
        setIsModalOpen(true);
    };
    const handleCloseModal = () => setIsModalOpen(false);

    const fetchAllDocs = useCallback(async (page, limit) => {
        setIsLoading(true);
        setError('');
        try {
            const response = await axios.get(`${API_BASE_URL}/all-doc-data?page=${page}&limit=${limit}`);
            if (response?.data) {
                setDocData(response.data.data || []);
                setCurrentPage(response.data.page || 1);
                setTotalPages(response.data.totalPages || 0);
                setTotalItems(response.data.totalItems || 0);
                 if (response.data.data.length === 0 && response.data.totalItems > 0 && page > 1) {
                    setCurrentPage(prev => Math.max(1, prev -1));
                }
            } else {
                setDocData([]);
                setTotalPages(0);
                setTotalItems(0);
                setError('No data received from server.');
            }
        } catch (err) {
            console.error("Fetch error:", err);
            setDocData([]);
            setTotalPages(0);
            setTotalItems(0);
            setError(err.response?.data?.message || err.message || 'Failed to fetch documents.');
        } finally {
            setIsLoading(false);
        }
    }, [itemsPerPage]); // itemsPerPage is a dependency if it can change, API_BASE_URL is stable

    useEffect(() => {
        fetchAllDocs(currentPage, itemsPerPage);
    }, [currentPage, fetchAllDocs]); // Removed itemsPerPage from here as it's in fetchAllDocs deps

    const handleUploadSuccess = () => {
        console.log("Document uploaded successfully via modal!");
        fetchAllDocs(1, itemsPerPage); // Always fetch page 1 after upload to see new item
        setCurrentPage(1);
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage && !isLoading) {
            setCurrentPage(newPage);
        }
    };
    
    // Pagination controls styled like the QuestionList example
    const PaginationControls = () => {
        if (!totalPages || totalPages <= 0) return null; // Hide if no pages or error

        const getPageNumbers = () => {
            const pageNumbers = [];
            const maxPagesToShow = 5; // Consistent with QuestionList or adjust as needed
            const halfMaxPages = Math.floor(maxPagesToShow / 2);

            if (totalPages <= maxPagesToShow) {
                for (let i = 1; i <= totalPages; i++) {
                    pageNumbers.push(i);
                }
            } else {
                let startPage = currentPage - halfMaxPages;
                let endPage = currentPage + halfMaxPages;

                if (startPage <= 0) {
                    endPage -= (startPage - 1);
                    startPage = 1;
                }
                if (endPage > totalPages) {
                    startPage -= (endPage - totalPages);
                    endPage = totalPages;
                }
                startPage = Math.max(startPage, 1); // Ensure startPage is at least 1

                if (startPage > 1) {
                    pageNumbers.push(1);
                    if (startPage > 2) {
                        pageNumbers.push('...');
                    }
                }
                for (let i = startPage; i <= endPage; i++) {
                    pageNumbers.push(i);
                }
                if (endPage < totalPages) {
                    if (endPage < totalPages - 1) {
                        pageNumbers.push('...');
                    }
                    pageNumbers.push(totalPages);
                }
            }
            return pageNumbers;
        };
        
        const pageDisplayInfo = totalItems > 0 ? `Page ${currentPage} of ${totalPages} (Total: ${totalItems} records)` : `Page ${currentPage} of ${totalPages}`;


        return (
            <div className="mt-6 flex flex-col sm:flex-row justify-between items-center bg-white px-4 py-3 border-t border-gray-200 rounded-b-lg">
                 <div className="text-sm text-gray-700 mb-2 sm:mb-0">
                   {pageDisplayInfo}
                </div>
                {totalPages > 1 && ( // Only show nav if more than one page
                    <nav className="inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1 || isLoading}
                            className={`relative inline-flex items-center px-3 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                            Previous
                        </button>
                        {getPageNumbers().map((page, index) => (
                            typeof page === 'number' ? (
                                <button
                                    key={index}
                                    onClick={() => handlePageChange(page)}
                                    disabled={isLoading}
                                    className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium
                                        ${currentPage === page ? 'z-10 bg-blue-50 border-blue-500 text-blue-600' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                                >
                                    {page}
                                </button>
                            ) : (
                                <span key={index} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                                    {page} {/* This will render "..." */}
                                </span>
                            )
                        ))}
                        <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages || isLoading}
                            className={`relative inline-flex items-center px-3 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                            Next
                        </button>
                    </nav>
                )}
            </div>
        );
    };


    return (
        <div className="p-4 md:p-6 bg-gray-50 min-h-full"> {/* Overall page background */}
            <div className=""> {/* Content container */}
                {/* Header Section */}
                <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-2">
                    <h1 className="text-2xl md:text-3xl font-semibold text-gray-800 mb-3 sm:mb-0">
                        Manage Documents
                    </h1>
                    <button
                        onClick={handleOpenModalForAdd}
                        className="px-5 py-2 bg-blue-600 text-white font-medium rounded-lg shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors text-sm"
                    >
                        Upload Document
                    </button>
                </div>

                {/* Modal for Uploading */}
                {isModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-60 overflow-y-auto h-full w-full flex items-center justify-center z-50 p-4">
                        <div className="bg-white p-0 rounded-lg shadow-xl w-full max-w-xl sm:max-w-2xl">
                            <SingleDocUpload
                                onClose={handleCloseModal}
                                onUploadSuccess={handleUploadSuccess}
                                isModal={true}
                            />
                        </div>
                    </div>
                )}
                
                {/* Error Display */}
                {error && <div className="mb-4 p-3 bg-red-100 text-red-700 border border-red-300 rounded-md shadow-sm">{error}</div>}

                {/* Table and Data Display Area */}
                <div className="bg-white shadow-md rounded-lg overflow-hidden">
                    {isLoading && docData.length === 0 ? (
                        <div className="p-10 text-center text-gray-500">
                            <svg className="animate-spin h-8 w-8 text-blue-600 mx-auto mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Loading documents...
                        </div>
                    ) : !isLoading && docData.length === 0 ? (
                         <div className="p-10 text-center text-gray-500">
                            <h3 className="text-lg font-medium">No Documents Found</h3>
                            <p className="text-sm">Use the "Upload Document" button to add new documents.</p>
                        </div>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-100"> {/* Lighter gray for table head, similar to reference */}
                                        <tr>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider" style={{width: '10%'}}>
                                                Sr No.
                                            </th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider" style={{width: '25%'}}>
                                                Lesson Number
                                            </th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider" style={{width: '65%'}}>
                                                Document Name
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {docData.map((item, index) => (
                                            <tr key={`${item.filename}-${item.lessonNumber}-${index}`} className={`hover:bg-gray-50 transition-colors ${isLoading ? 'opacity-60' : ''}`}>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-700">
                                                    {(currentPage - 1) * itemsPerPage + index + 1}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                                    {item?.lessonNumber}
                                                </td>
                                                <td className="px-6 py-4 whitespace-pre-wrap break-words text-sm text-gray-700 max-w-md sm:max-w-lg md:max-w-2xl"> {/* Adjusted max-width */}
                                                    {item?.filename}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <PaginationControls /> {/* Render pagination controls here */}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ManageDoc;